import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import type { CaseRow, CaseType, CustomFormSummary, DocSummary, Meta, Patient, SessionUser } from "../types";
import { initials, ruAt, ruDateTime, year } from "../docs/helpers";
import { useMessage } from "./Message";
import CaseForm from "./CaseForm";
import type { DocOverlayRequest } from "../docs/DocOverlay";
import { DOC_IDS } from "../docs/generated/ids";
import FilePreview, { FILE_ACCEPT, fileSize } from "./FilePreview";
import FileImportDialog from "./FileImportDialog";

// Окно документа и мастер истории тянут за собой все 30 форм — грузятся при
// первом открытии, а не вместе со списком пациентов.
const DocOverlay = lazy(() => import("../docs/DocOverlay"));
const HistoryWizard = lazy(() => import("./HistoryWizard"));

/** Port of ajax/pat_click.php — the right-hand column for one patient. */
export default function CasesPanel({
  patient,
  user,
  meta,
}: {
  patient: Patient;
  user: SessionUser;
  meta: Meta;
}) {
  const message = useMessage();
  const [cases, setCases] = useState<CaseRow[] | null>(null);
  const [checkedDocs, setCheckedDocs] = useState<Set<number>>(new Set());
  const [checkedCases, setCheckedCases] = useState<Set<number>>(new Set());
  const [checkedForms, setCheckedForms] = useState<Set<string>>(new Set());
  const [caseForm, setCaseForm] = useState<{ type: CaseType; row: CaseRow | null } | null>(null);
  const [overlay, setOverlay] = useState<DocOverlayRequest | null>(null);
  const [uniteOpen, setUniteOpen] = useState(false);
  /** Раскрытые случаи. По умолчанию все свёрнуты: у пациента с историей их
      много, и страница начинается с оглавления, а не с простыни документов —
      операция и даты видны прямо в заголовке. */
  const [opened, setOpened] = useState<Set<number>>(new Set());
  const [wizardCase, setWizardCase] = useState<CaseRow | null>(null);
  /** Свои документы клиники (Админ → «Документы») — печатаются рядом со встроенными. */
  const [customForms, setCustomForms] = useState<CustomFormSummary[]>([]);
  /** Импорт файлов: в какой случай выбирают файл, и выбранные — в окне, где им дают имена. */
  const fileInput = useRef<HTMLInputElement>(null);
  const importCase = useRef<CaseRow | null>(null);
  const [importing, setImporting] = useState<{ row: CaseRow; files: File[] } | null>(null);
  /** Открытый просмотр импортированного файла. */
  const [preview, setPreview] = useState<DocSummary | null>(null);

  useEffect(() => {
    api
      .customForms()
      // Исправленный вид встроенного документа — не отдельный документ: он
      // открывается кнопкой самого документа (см. docs/builtin.tsx).
      .then((r) => setCustomForms(r.forms.filter((f) => !f.docs_id)))
      .catch(() => setCustomForms([]));
  }, []);

  /**
   * silent — перечитать, не убирая список с экрана: после сохранения или
   * закрытия документа дерево случаев остаётся на месте (и прокрутка тоже),
   * а строки подменяются, когда придёт ответ. Без silent (смена пациента) —
   * как раньше, с загрузчиком.
   */
  // Быстро щёлкнули по двум пациентам — ответ первого не должен лечь под
  // шапку второго (и его документы не должны удаляться под чужим именем).
  const loadSeq = useRef(0);
  const load = useCallback(async (silent = false) => {
    const seq = ++loadSeq.current;
    if (!silent) setCases(null);
    try {
      const res = await api.cases(patient.pat_id);
      if (seq !== loadSeq.current) return;
      setCases(res.cases);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      message(err as Error);
      setCases([]);
    }
  }, [patient.pat_id, message]);

  useEffect(() => {
    setCheckedDocs(new Set());
    setCheckedCases(new Set());
    setCheckedForms(new Set());
    setOpened(new Set());
    load();
  }, [load]);

  const toggle = <T,>(set: Set<T>, value: T) => {
    const next = new Set(set);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  };

  /** Checking a case selects every document in it, as the legacy checkbox did. */
  function toggleCase(row: CaseRow) {
    const nextCases = toggle(checkedCases, row.case_id);
    setCheckedCases(nextCases);
    const nextDocs = new Set(checkedDocs);
    for (const d of row.docs) {
      if (nextCases.has(row.case_id)) nextDocs.add(d.doc_id);
      else nextDocs.delete(d.doc_id);
    }
    setCheckedDocs(nextDocs);
  }

  async function deleteChecked() {
    if (checkedDocs.size === 0) return;
    if (!confirm(`Удалить выбранные записи (${checkedDocs.size})?`)) return;
    try {
      await api.deleteDocs([...checkedDocs]);
    } catch (err) {
      message(err as Error);
      return;
    }
    setCheckedDocs(new Set());
    setCheckedCases(new Set());
    message("Записи удалены");
    load(true);
  }

  async function deleteCase(row: CaseRow) {
    if (!confirm("Удалить случай? Записи внутри останутся в базе.")) return;
    try {
      await api.deleteCase(row.case_id);
    } catch (err) {
      message(err as Error);
      return;
    }
    message("Случай удалён");
    load(true);
  }

  async function unite(keep: number) {
    const ids = [...checkedCases];
    if (ids.length < 2) return;
    if (!confirm("Объединить выбранные случаи? Документы перейдут к выбранному случаю.")) return;
    try {
      await api.uniteCases(keep, ids);
    } catch (err) {
      message(err as Error);
      return;
    }
    setUniteOpen(false);
    setCheckedCases(new Set());
    message("Случаи объединены");
    load(true);
  }

  /** «Импорт» — окно выбора файла для этого случая. */
  function pickFiles(row: CaseRow) {
    importCase.current = row;
    fileInput.current?.click();
  }

  /** Выбранные файлы — в окно импорта: там их можно переименовать до загрузки. */
  function filesPicked(list: FileList | null) {
    const row = importCase.current;
    const files = Array.from(list ?? []);
    if (fileInput.current) fileInput.current.value = "";
    if (row && files.length) setImporting({ row, files });
  }

  /** Загрузка из окна импорта закончилась: показать добавленные; всё ушло — окно закрыть. */
  function filesImported(added: number, all: boolean) {
    const caseId = importing?.row.case_id;
    if (added && caseId) {
      message(added === 1 ? "Файл добавлен в случай" : `Файлы добавлены в случай: ${added}`);
      setOpened((prev) => new Set(prev).add(caseId));
      load(true);
    }
    if (all) setImporting(null);
  }

  /** Отмеченные импортированные файлы — в общую печать документов они не входят. */
  const fileDocIds = useMemo(
    () => new Set((cases ?? []).flatMap((c) => c.docs.filter((d) => d.file).map((d) => d.doc_id))),
    [cases]
  );
  function printChecked() {
    if (!checkedDocs.size) return;
    const docIds = [...checkedDocs].filter((id) => !fileDocIds.has(id));
    if (docIds.length < checkedDocs.size) {
      message("Импортированные файлы печатаются из своего просмотра: «Просмотр» → «Печать»", "ok", { ms: 6000 });
    }
    if (docIds.length) setOverlay({ kind: "print-docs", docIds, patId: patient.pat_id });
  }

  // Ключи своих документов — "cf:<id>": один список, одни галочки, одна
  // пакетная печать со встроенными формами.
  const printForms = useMemo(
    () => [
      ...Object.entries(meta.printForms),
      ...customForms
        .filter((f) => f.show_forms)
        .map((f) => [`cf:${f.form_id}`, f.name] as [string, string]),
    ],
    [meta.printForms, customForms]
  );
  const checkedCaseRows = (cases ?? []).filter((c) => checkedCases.has(c.case_id));

  return (
    <>
      <div className="docs-pat-name">{patient.fio}</div>
      <div className="docs-pat-info">
        {patient.trustee ? (
          <>
            <strong>Представитель:</strong> {patient.trustee}
            <br />
          </>
        ) : null}
        {patient.town ? (
          <>
            <strong>{patient.town}</strong>,{" "}
          </>
        ) : null}
        {year(patient.bdate)} г.р.
      </div>

      <div className="docs-header">Осмотры и протоколы</div>
      <div className="docs-buttons">
        <div
          className="docs-buttons-one docs-buttons-case"
          onClick={() => setCaseForm({ type: "a", row: null })}
        >
          Амбулат
        </div>
        <div
          className="docs-buttons-one docs-buttons-case"
          onClick={() => setCaseForm({ type: "s", row: null })}
        >
          Стацион
        </div>
        <div
          className={`docs-buttons-one docs-buttons-print${checkedDocs.size ? "" : " disabled"}`}
          onClick={printChecked}
        >
          Печатать
        </div>
        <div
          className={`docs-buttons-one docs-buttons-del${checkedDocs.size ? "" : " disabled"}`}
          onClick={deleteChecked}
        >
          Удалить
        </div>

        {user.is_admin && (
          <div className="docs-buttons-unite__wrapper">
            <div
              className={`docs-buttons-one docs-buttons-unite${
                checkedCases.size > 1 ? "" : " disabled"
              }`}
              onClick={() => checkedCases.size > 1 && setUniteOpen((v) => !v)}
            >
              Объединить
            </div>
            {uniteOpen && (
              <div className="docs-buttons-unite__modal" style={{ display: "block" }}>
                <div className="docs-buttons-unite__warn">
                  Выберите случай, к которому перенести документы.
                </div>
                <div className="docs-buttons-unite__buttons">
                  {checkedCaseRows.map((c) => (
                    <div key={c.case_id} className="button" onClick={() => unite(c.case_id)}>
                      #{c.number || c.case_id}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {caseForm && !caseForm.row && (
        <CaseForm
          patId={patient.pat_id}
          user={user}
          type={caseForm.type}
          row={null}
          onClose={() => setCaseForm(null)}
          onSaved={() => {
            setCaseForm(null);
            message("Случай добавлен");
            load(true);
          }}
        />
      )}

      {cases === null && <div className="docs-loader" style={{ display: "block" }} />}

      {cases?.map((row) => {
        const protocols = Object.entries(meta.protocols[row.type] ?? {});
        return (
          <div key={row.case_id}>
            <div
              className="case"
              data-type={row.type}
              data-id={row.case_id}
              onClick={(e) => {
                // Whole-block click folds the case, as in the legacy UI —
                // unless the click landed on a control inside it.
                const el = e.target as HTMLElement;
                if (el.closest("input, label, .case-del, .case-edit, .docs-case-add")) return;
                setOpened((prev) => toggle(prev, row.case_id));
              }}
            >
              {/* База .case > .arrow уже повёрнута на 180° («раскрыт») —
                  свёрнутому возвращается 0°, и стрелка наконец различает
                  состояния. */}
              <div
                className="arrow"
                style={opened.has(row.case_id) ? undefined : { transform: "rotate(0deg)" }}
              />
              <div className="case-header">
                <input
                  id={`case-print-${row.case_id}`}
                  type="checkbox"
                  checked={checkedCases.has(row.case_id)}
                  onChange={() => toggleCase(row)}
                />
                <label htmlFor={`case-print-${row.case_id}`}>
                  <span />
                </label>
                {row.type === "a" ? "Амбулаторный" : "Стационарный"} случай
                {row.number ? <span className="inner-number"> #{row.number}</span> : null}
                {/* Операция — из протокола операции случая: свёрнутый случай
                    называет себя сам, без раскрытия документов. */}
                {row.oper_name ? <span className="case-oper">{row.oper_name}</span> : null}
              </div>
              {row.about ? <div className="case-about">{row.about}</div> : null}
              <div className="case-dates">
                {user.is_admin && row.added_by ? (
                  <div>
                    <strong>Добавил(а):</strong> {initials(row.added_by)} — {ruAt(row.created_at)}
                  </div>
                ) : null}
                {user.is_admin ? (
                  <>
                    <strong>ID случая:</strong>{" "}
                    <span className="case-dates__id">{row.case_id}</span>
                    <br />
                  </>
                ) : null}
                {row.type === "a" ? (
                  <>
                    <strong>Дата заполнения медкарты:</strong> {ruAt(row.date_in)}
                  </>
                ) : (
                  <>
                    <strong>Дата поступления:</strong> {ruAt(row.date_in)}
                    {row.date_out ? (
                      <>
                        <br />
                        <strong>Дата выписки:</strong> {ruAt(row.date_out)}
                      </>
                    ) : null}
                  </>
                )}
              </div>
              {user.can_del_case && (
                <div className="case-del" title="Удалить случай" onClick={() => deleteCase(row)} />
              )}
              <div
                className={`case-edit${user.can_del_case ? "" : " case-edit-right"}`}
                title="Изменить случай"
                onClick={() => setCaseForm({ type: row.type, row })}
              />
              {caseForm?.row?.case_id === row.case_id && (
                <CaseForm
                  patId={patient.pat_id}
                  user={user}
                  type={caseForm.type}
                  row={caseForm.row}
                  onClose={() => setCaseForm(null)}
                  onSaved={() => {
                    setCaseForm(null);
                    message("Случай изменён");
                    load(true);
                  }}
                />
              )}
            </div>

            {opened.has(row.case_id) && (<>
            <div className="case-buttons">
              {/* Только стационар: в амбулаторном случае два-три документа без
                  цепочки между ними — мастер был бы медленнее кнопок. */}
              {row.type === "s" && (
                <div
                  className="case-buttons-hist"
                  title="Заполнить случай готовым набором документов"
                  onClick={() => setWizardCase(row)}
                >
                  Шаблон истории
                </div>
              )}
              {/* Свои документы, назначенные в этот тип карты: заполняются и
                  сохраняются в случай, как протоколы. */}
              {customForms
                .filter((f) => (row.type === "a" ? f.show_amb : f.show_stat))
                .map((f) => (
                  <div
                    key={`cf${f.form_id}`}
                    title={`${f.name} — свой документ клиники`}
                    onClick={() =>
                      setOverlay({
                        kind: "custom-form",
                        formId: f.form_id,
                        patId: patient.pat_id,
                        caseId: row.case_id,
                        docId: null,
                      })
                    }
                  >
                    {f.name}
                  </div>
                ))}
              {protocols.map(([key, labels]) => (
                <div
                  key={key}
                  id={key}
                  title={DOC_IDS.has(key) ? labels[1] : `${labels[1]} — форма не перенесена`}
                  className={DOC_IDS.has(key) ? undefined : "disabled"}
                  onClick={() =>
                    DOC_IDS.has(key) &&
                    setOverlay({
                      kind: "form",
                      mode: "add",
                      docsId: key,
                      patId: patient.pat_id,
                      caseId: row.case_id,
                      docId: null,
                    })
                  }
                >
                  {labels[0]}
                </div>
              ))}
              {/* Последней — «Импорт»: PDF, Word, фото, скан становятся
                  записью случая с просмотром и печатью. */}
              <div
                className="case-buttons-import"
                title="Добавить в случай файл: PDF, DOC, DOCX, RTF, фото, скан — до 25 МБ"
                onClick={() => pickFiles(row)}
              >
                Импорт
              </div>
            </div>

            {row.docs.length === 0 ? (
              <div className="case-no">Нет записей.</div>
            ) : (
              <div className="docs-rows docs-rows-docforms">
                {row.docs.map((doc, i) => {
                  const labels = meta.protocols[row.type]?.[doc.docs_id];
                  const cfName = doc.docs_id.startsWith("cf:")
                    ? customForms.find((f) => `cf:${f.form_id}` === doc.docs_id)?.name ??
                      "Свой документ"
                    : null;
                  const first = i === 0 || row.docs[i - 1].docs_id !== doc.docs_id;
                  return (
                    <div key={doc.doc_id}>
                      {first && <div className="docs-rows-devider" />}
                      <div className="docs-row" data-doc-id={doc.doc_id}>
                        <div className="col-1">
                          <input
                            id={`g${doc.doc_id}`}
                            type="checkbox"
                            checked={checkedDocs.has(doc.doc_id)}
                            onChange={() => setCheckedDocs(toggle(checkedDocs, doc.doc_id))}
                          />
                          <label htmlFor={`g${doc.doc_id}`}>
                            <span />
                          </label>
                        </div>
                        <div className="col-2">
                          {doc.file ? (
                            <>
                              <span className="docs-file-name">{doc.file.name}</span>
                              <span>
                                Файл добавлен {ruAt(doc.created_at)} · {fileSize(doc.file.size)}
                              </span>
                            </>
                          ) : (
                            <>
                              {cfName ?? labels?.[1] ?? doc.docs_id}
                              <span>Запись добавлена {ruAt(doc.created_at)}</span>
                            </>
                          )}
                        </div>
                        <div className="col-3">
                          {doc.file ? (
                            <div className="docs-row-print docs-row-print-case" onClick={() => setPreview(doc)}>
                              Просмотр
                            </div>
                          ) : (
                          <div
                            className="docs-row-print docs-row-print-case"
                            onClick={() =>
                              doc.docs_id.startsWith("cf:")
                                ? setOverlay({
                                    kind: "custom-form",
                                    formId: Number(doc.docs_id.slice(3)),
                                    patId: patient.pat_id,
                                    caseId: row.case_id,
                                    docId: doc.doc_id,
                                  })
                                : DOC_IDS.has(doc.docs_id)
                                  ? setOverlay({
                                      kind: "form",
                                      mode: "edit",
                                      docsId: doc.docs_id,
                                      patId: patient.pat_id,
                                      caseId: row.case_id,
                                      docId: doc.doc_id,
                                    })
                                  : message("Форма этого документа ещё не перенесена", "error")
                            }
                          >
                            Печать/Ред
                          </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </>)}
          </div>
        );
      })}

      {cases?.length === 0 && (
        <div className="docs-cases">
          <div className="docs-no" style={{ display: "block" }}>
            Список осмотров пуст.
            <br />
            Добавьте амбулаторный или стационарный случай.
          </div>
        </div>
      )}

      <div className="docs-header docs-header-printforms">
        <input
          id="printforms"
          type="checkbox"
          checked={checkedForms.size === printForms.length && printForms.length > 0}
          onChange={(e) =>
            setCheckedForms(e.target.checked ? new Set(printForms.map(([k]) => k)) : new Set())
          }
        />
        <label htmlFor="printforms">
          <span />
        </label>
        Печатные формы
      </div>
      <div className="docs-buttons">
        <div
          className={checkedForms.size ? "docs-printforms-print" : "disabled docs-printforms-print"}
          onClick={() =>
            checkedForms.size &&
            setOverlay({ kind: "print-forms", formIds: [...checkedForms], patId: patient.pat_id })
          }
        >
          Печатать
        </div>
      </div>
      <div className="docs-rows docs-rows-printforms">
        {printForms.map(([key, label], i) => (
          <div className="docs-row" key={key}>
            <div className="col-1">
              <input
                id={`f${i + 1}`}
                type="checkbox"
                checked={checkedForms.has(key)}
                onChange={() => setCheckedForms(toggle(checkedForms, key))}
              />
              <label htmlFor={`f${i + 1}`}>
                <span />
              </label>
            </div>
            <div className="col-2">{label}</div>
            <div className="col-3">
              <div
                className="docs-row-print"
                onClick={() =>
                  key.startsWith("cf:")
                    ? setOverlay({ kind: "print-forms", formIds: [key], patId: patient.pat_id })
                    : DOC_IDS.has(key)
                      ? setOverlay({
                          kind: "form",
                          mode: "add",
                          docsId: key,
                          patId: patient.pat_id,
                          caseId: null,
                          docId: null,
                        })
                      : message("Форма этого документа ещё не перенесена", "error")
                }
              >
                Печать
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Одно окно выбора на панель: «Импорт» случая запоминает, куда класть. */}
      <input
        ref={fileInput}
        type="file"
        multiple
        accept={FILE_ACCEPT}
        style={{ display: "none" }}
        onChange={(e) => filesPicked(e.target.files)}
      />

      {importing && (
        <FileImportDialog
          caseId={importing.row.case_id}
          caseLabel={`${importing.row.type === "a" ? "Амбулаторный" : "Стационарный"} случай${
            importing.row.number ? ` #${importing.row.number}` : ""
          }`}
          files={importing.files}
          onClose={() => setImporting(null)}
          onDone={filesImported}
        />
      )}

      {preview?.file && (
        <FilePreview docId={preview.doc_id} file={preview.file} onClose={() => setPreview(null)} />
      )}

      {wizardCase && (
        <Suspense fallback={<div className="overlay"><div className="overlay-content-loader" /></div>}>
        <HistoryWizard
          patient={patient}
          caseRow={wizardCase}
          user={user}
          meta={meta}
          onClose={() => setWizardCase(null)}
          onDone={() => {
            setWizardCase(null);
            load(true);
          }}
        />
        </Suspense>
      )}

      {overlay && (
        <Suspense fallback={<div className="overlay"><div className="overlay-content-loader" /></div>}>
        <DocOverlay
          request={overlay}
          user={user}
          meta={meta}
          onClose={() => {
            setOverlay(null);
            // Even a closed-without-saving overlay can leave the tree stale
            // (печать сохраняет и закрывает) — refresh on close so it appears.
            load(true);
          }}
          onSaved={() => {
            setOverlay(null);
            load(true);
          }}
        />
        </Suspense>
      )}
    </>
  );
}
